import type { CSSProperties, FC } from 'react';
import { Divider, Dropdown, Menu, Typography } from 'antd';
import { TbAdjustmentsHorizontal } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import {
    DropdownButton,
    DropdownButtonOption,
    DropdownMenuLabel,
    DropdownOptionContent,
    DropdownPanel,
    DropdownPanelMenu,
} from '@/shared/components/dropdowns';
import { SmallTooltip } from '@/shared/components/tooltips';
import {
    DEFAULT_FINANCIAL_VALUE_DISPLAY,
    FINANCIAL_VALUE_DECIMAL_PLACES,
    FinancialValueDecimalPlaces,
    FinancialValueDisplayPreference,
    FinancialValueDisplayScale,
    FinancialValueNegativeStyle,
} from '@/types/financialValueDisplay';

import { FinancialValue } from './FinancialValue';

interface FinancialValueDisplayControlProps {
    allowInherit?: boolean;
    disabled?: boolean;
    inheritDescription?: string;
    inheritLabel?: string;
    inheritSectionLabel?: string;
    loading?: boolean;
    resolvedValue?: FinancialValueDisplayPreference;
    variant?: 'compact' | 'preview';
    value?: FinancialValueDisplayPreference | null;
    onChange: (value: FinancialValueDisplayPreference | null) => void | Promise<void>;
}

const SCALE_OPTIONS: Array<DropdownButtonOption<FinancialValueDisplayScale>> = [
    { description: 'Choose k, mln, bln, or tln from each value.', label: 'Automatic', value: 'auto' },
    { description: 'Show the complete unscaled value.', label: 'Full Value', value: 'units' },
    { description: 'Present monetary amounts in thousands.', label: 'Thousands (k)', value: 'thousands' },
    { description: 'Present monetary amounts in millions.', label: 'Millions (mln)', value: 'millions' },
    { description: 'Present monetary amounts in billions.', label: 'Billions (bln)', value: 'billions' },
    { description: 'Present monetary amounts in trillions.', label: 'Trillions (tln)', value: 'trillions' },
];

const PRECISION_OPTIONS: Array<DropdownButtonOption<FinancialValueDecimalPlaces>> = FINANCIAL_VALUE_DECIMAL_PLACES.map(
    (decimalPlaces) => ({
        description:
            decimalPlaces === 0 ? 'Round displayed values to a whole unit.' : `Show up to ${decimalPlaces} decimals.`,
        label:
            decimalPlaces === 0 ? 'Whole Numbers' : `Up to ${decimalPlaces} Decimal${decimalPlaces === 1 ? '' : 's'}`,
        value: decimalPlaces,
    }),
);

const NEGATIVE_OPTIONS: Array<DropdownButtonOption<FinancialValueNegativeStyle>> = [
    { description: 'Example: ($3.15 bln)', label: 'Accounting', value: 'accounting' },
    { description: 'Example: -$3.15 bln', label: 'Minus Sign', value: 'minus' },
];

const FINANCIAL_VALUE_MENU_ITEM_STYLE: CSSProperties = {
    height: 'auto',
    lineHeight: 'normal',
    marginBlock: '0.125rem',
    paddingBlock: '0.25rem',
    paddingInline: '0.75rem',
};

const FINANCIAL_VALUE_OPTION_DETAIL_STYLE: CSSProperties = { lineHeight: '1rem' };

export const FinancialValueDisplayControl: FC<FinancialValueDisplayControlProps> = ({
    allowInherit = false,
    disabled = false,
    inheritDescription = 'Follow the workspace default without changing its configuration.',
    inheritLabel = 'Use Inherited Default',
    inheritSectionLabel = 'Account Preference',
    loading = false,
    onChange,
    resolvedValue = DEFAULT_FINANCIAL_VALUE_DISPLAY,
    variant = 'preview',
    value,
}) => {
    const { token } = useAntDesignTheme();
    const effectiveValue = value ?? resolvedValue;
    const update = (updates: Partial<FinancialValueDisplayPreference>) =>
        onChange({ ...(value ?? effectiveValue), ...updates });
    const triggerButton = (
        <DropdownButton
            aria-label='Configure financial value display'
            disabled={disabled}
            loading={loading}
            size={variant === 'compact' ? 'small' : undefined}
            style={{
                fontWeight: token.fontWeightStrong,
            }}
            trailingIcon={variant === 'compact' ? null : undefined}
            type={variant === 'compact' ? 'text' : undefined}
        >
            {variant === 'compact' ? (
                <TbAdjustmentsHorizontal style={{ color: token.colorTextTertiary }} />
            ) : (
                <FinancialValue preference={effectiveValue} value={-3_150_125_000.45} />
            )}
        </DropdownButton>
    );

    const dropdown = (
        <Dropdown
            disabled={disabled || loading}
            placement='bottomRight'
            trigger={['click']}
            popupRender={() => (
                <DropdownPanel maxHeight='min(28rem, 70vh)' style={{ marginTop: '0.375rem' }} width='20rem'>
                    {allowInherit ? (
                        <>
                            <DropdownMenuLabel>{inheritSectionLabel}</DropdownMenuLabel>
                            <DropdownPanelMenu
                                aria-label={`${inheritSectionLabel} options`}
                                selectable={false}
                                style={{ maxHeight: 'none', overflow: 'visible' }}
                                onClick={({ key }) => {
                                    if (key === 'inherit') {
                                        void onChange(null);
                                    }
                                }}
                            >
                                <Menu.Item key='inherit' style={FINANCIAL_VALUE_MENU_ITEM_STYLE}>
                                    <DropdownOptionContent
                                        description={inheritDescription}
                                        detailStyle={FINANCIAL_VALUE_OPTION_DETAIL_STYLE}
                                        selected={!value}
                                        title={inheritLabel}
                                    />
                                </Menu.Item>
                            </DropdownPanelMenu>
                            <Divider style={{ margin: 0 }} />
                        </>
                    ) : null}
                    <DropdownMenuLabel>Magnitude</DropdownMenuLabel>
                    <DropdownPanelMenu
                        aria-label='Magnitude options'
                        selectable={false}
                        style={{ maxHeight: 'none', overflow: 'visible' }}
                        onClick={({ key }) => {
                            const option = SCALE_OPTIONS.find(({ value: optionValue }) => optionValue === key);

                            if (option) {
                                void update({ scale: option.value });
                            }
                        }}
                    >
                        {SCALE_OPTIONS.map((option) => (
                            <Menu.Item key={option.value} style={FINANCIAL_VALUE_MENU_ITEM_STYLE}>
                                <DropdownOptionContent
                                    description={option.description}
                                    detailStyle={FINANCIAL_VALUE_OPTION_DETAIL_STYLE}
                                    selected={effectiveValue.scale === option.value}
                                    title={option.label}
                                />
                            </Menu.Item>
                        ))}
                    </DropdownPanelMenu>
                    <Divider style={{ margin: 0 }} />
                    <DropdownMenuLabel>Precision</DropdownMenuLabel>
                    <DropdownPanelMenu
                        aria-label='Precision options'
                        selectable={false}
                        style={{ maxHeight: 'none', overflow: 'visible' }}
                        onClick={({ key }) => {
                            const option = PRECISION_OPTIONS.find(
                                ({ value: optionValue }) => String(optionValue) === key,
                            );

                            if (option) {
                                void update({ decimalPlaces: option.value });
                            }
                        }}
                    >
                        {PRECISION_OPTIONS.map((option) => (
                            <Menu.Item key={option.value} style={FINANCIAL_VALUE_MENU_ITEM_STYLE}>
                                <DropdownOptionContent
                                    description={option.description}
                                    detailStyle={FINANCIAL_VALUE_OPTION_DETAIL_STYLE}
                                    selected={effectiveValue.decimalPlaces === option.value}
                                    title={option.label}
                                />
                            </Menu.Item>
                        ))}
                    </DropdownPanelMenu>
                    <Divider style={{ margin: 0 }} />
                    <DropdownMenuLabel>Negative Values</DropdownMenuLabel>
                    <DropdownPanelMenu
                        aria-label='Negative value options'
                        selectable={false}
                        style={{ maxHeight: 'none', overflow: 'visible' }}
                        onClick={({ key }) => {
                            const option = NEGATIVE_OPTIONS.find(({ value: optionValue }) => optionValue === key);

                            if (option) {
                                void update({ negativeStyle: option.value });
                            }
                        }}
                    >
                        {NEGATIVE_OPTIONS.map((option) => (
                            <Menu.Item key={option.value} style={FINANCIAL_VALUE_MENU_ITEM_STYLE}>
                                <DropdownOptionContent
                                    description={option.description}
                                    detailStyle={FINANCIAL_VALUE_OPTION_DETAIL_STYLE}
                                    selected={effectiveValue.negativeStyle === option.value}
                                    title={option.label}
                                />
                            </Menu.Item>
                        ))}
                    </DropdownPanelMenu>
                </DropdownPanel>
            )}
        >
            {variant === 'compact' ? (
                <span style={{ display: 'inline-flex' }}>
                    <SmallTooltip placement='topLeft' title='Format Financial Values'>
                        {triggerButton}
                    </SmallTooltip>
                </span>
            ) : (
                triggerButton
            )}
        </Dropdown>
    );

    if (variant === 'compact') {
        return dropdown;
    }

    return (
        <div
            aria-live='polite'
            style={{
                alignItems: 'center',
                background: token.colorFillQuaternary,
                border: `1px solid ${token.colorBorderSecondary}`,
                borderRadius: token.borderRadiusSM,
                display: 'flex',
                gap: '0.5rem',
                justifyContent: 'space-between',
                padding: '0.5rem 0.625rem',
            }}
        >
            <Typography.Text style={{ flex: 'none', whiteSpace: 'nowrap' }} type='secondary'>
                Preview
            </Typography.Text>
            {dropdown}
        </div>
    );
};
